import { motion } from "framer-motion";
import {
  UserCircle,
  Buildings,
  PaperPlaneRight,
  CurrencyDollar,
  Clock,
  WhatsappLogo,
  Warning,
} from "@phosphor-icons/react";
import { TELEGRAM_URL, TELEGRAM_REP_URL } from "@/App";

const WHATSAPP_NUMBER = "+224625908563";
const WHATSAPP_URL = "https://wa.me/224625908563";

export const DualContact = () => {
  return (
    <section
      id="contact"
      data-testid="dual-contact-section"
      className="border-t border-border bg-background"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-36">
        <div className="mb-16 max-w-3xl">
          <span className="font-code text-xs uppercase tracking-[0.2em] text-accent">
            [ 05 ] — Get in touch
          </span>
          <h2 className="font-display font-bold tracking-tight leading-none text-4xl sm:text-5xl lg:text-6xl mt-5">
            Two dedicated channels.
          </h2>
          <p className="font-body text-muted-foreground mt-6 max-w-xl leading-relaxed">
            Choose the channel that fits your situation. Both are handled
            personally — please note responses may take a little longer during
            periods of high volume.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 border-t border-l border-border">
          {/* Client Lane */}
          <motion.div
            data-testid="contact-client-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="group border-r border-b border-border p-8 sm:p-12 flex flex-col hover:bg-card transition-colors duration-300 relative"
          >
            <div className="absolute top-0 left-0 h-[2px] w-0 bg-accent group-hover:w-full transition-all duration-500" />
            <div className="flex items-start justify-between mb-10">
              <UserCircle size={44} weight="duotone" className="text-foreground" />
              <span className="font-code text-xs uppercase tracking-[0.2em] text-muted-foreground">
                For clients
              </span>
            </div>
            <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mb-4">
              Need your account resolved?
            </h3>
            <p className="font-body text-muted-foreground leading-relaxed mb-10 max-w-md">
              Disabled, suspended, restricted, or something else affecting your
              account. Message me with the details and I&apos;ll take it from
              there.
            </p>
            <div className="mt-auto">
              <a
                href={TELEGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-client-button"
                className="inline-flex items-center gap-3 bg-foreground text-background px-7 py-4 font-code text-sm uppercase tracking-[0.15em] font-medium hover:bg-accent hover:text-accent-foreground transition-colors duration-200 group/btn"
              >
                @cranielz
                <PaperPlaneRight
                  size={18}
                  weight="fill"
                  className="group-hover/btn:translate-x-1 transition-transform duration-200"
                />
              </a>
            </div>
          </motion.div>

          {/* Representative Lane */}
          <motion.div
            data-testid="contact-rep-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="group border-r border-b border-border p-8 sm:p-12 flex flex-col bg-accent/[0.04] hover:bg-accent/[0.08] transition-colors duration-300 relative"
          >
            <div className="absolute top-0 left-0 h-[2px] w-full bg-accent" />
            <div className="flex items-start justify-between mb-10">
              <Buildings size={44} weight="duotone" className="text-accent" />
              <span className="font-code text-xs uppercase tracking-[0.2em] text-accent">
                For platform reps
              </span>
            </div>
            <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mb-4">
              Representative from Meta, Google or another platform?
            </h3>
            <p className="font-body text-muted-foreground leading-relaxed mb-8 max-w-md">
              A dedicated channel for verified platform employees and support
              representatives. Priority handling and faster turnaround for
              legitimate collaboration.
            </p>

            <div className="border border-border bg-background/40 p-5 mb-10 max-w-md">
              <div className="flex items-center gap-3 mb-2">
                <CurrencyDollar size={20} weight="bold" className="text-accent" />
                <span className="font-code text-xs uppercase tracking-[0.2em] text-foreground font-medium">
                  Compensation
                </span>
              </div>
              <p className="font-body text-sm text-muted-foreground leading-relaxed">
                Payouts start from{" "}
                <span className="font-code text-foreground font-medium">
                  $1,000+
                </span>{" "}
                per case, scaled to complexity and priority.
              </p>
            </div>

            <div className="mt-auto">
              <a
                href={TELEGRAM_REP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-rep-button"
                className="inline-flex items-center gap-3 bg-accent text-accent-foreground px-7 py-4 font-code text-sm uppercase tracking-[0.15em] font-medium hover:bg-blue-500 transition-colors duration-200 group/btn"
              >
                @cranieltalks
                <PaperPlaneRight
                  size={18}
                  weight="fill"
                  className="group-hover/btn:translate-x-1 transition-transform duration-200"
                />
              </a>
            </div>
          </motion.div>
        </div>

        <div className="mt-10 flex items-start gap-3 text-muted-foreground max-w-2xl">
          <Clock size={18} className="mt-0.5 shrink-0" />
          <p className="font-code text-xs uppercase tracking-[0.15em] leading-relaxed">
            Due to a high volume of requests, replies may take longer than
            usual. Every message is read and answered personally.
          </p>
        </div>

        {/* WhatsApp fallback */}
        <div
          data-testid="whatsapp-fallback"
          className="mt-8 border border-border bg-card/40 p-6 sm:p-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-start"
        >
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-3">
              <WhatsappLogo size={26} weight="duotone" className="text-accent" />
              <span className="font-code text-[11px] uppercase tracking-[0.2em] text-accent">
                WhatsApp — fallback only
              </span>
            </div>
            <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight mb-3">
              Only if you cannot reach me on Telegram.
            </h3>
            <p className="font-body text-sm text-muted-foreground leading-relaxed max-w-xl">
              WhatsApp is a backup channel for cases where you&apos;ve waited
              and still haven&apos;t received a reply on Telegram. Please give
              Telegram time first — that&apos;s where cases are handled.
            </p>

            <div className="mt-5 flex items-start gap-3 border border-red-500/40 bg-red-500/[0.04] px-4 py-3 max-w-xl">
              <Warning
                size={16}
                weight="fill"
                className="text-red-400 mt-0.5 shrink-0"
              />
              <p className="font-code text-[11px] uppercase tracking-[0.15em] text-red-300 leading-relaxed">
                Do not spam. Repeated or duplicate messages will result in an
                immediate block without warning.
              </p>
            </div>
          </div>

          <div className="md:col-span-5 md:pl-6 md:border-l md:border-border flex items-center h-full">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-button"
              className="inline-flex items-center justify-center gap-3 border border-border bg-background hover:border-accent hover:text-accent px-6 py-4 font-code text-xs uppercase tracking-[0.15em] font-medium transition-colors duration-200 group w-full sm:w-fit"
            >
              <WhatsappLogo size={16} weight="fill" />
              Tap to connect on WhatsApp
              <PaperPlaneRight
                size={14}
                weight="fill"
                className="group-hover:translate-x-1 transition-transform duration-200"
              />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DualContact;
